import Component from '@glimmer/component'
import t from 'ember-intl/helpers/t'
import { and } from 'ember-truth-helpers'
import Icon from 'customer-flows/components/ui-kit/icon'
import stopClickPropagation from 'customer-flows/modifiers/stop-click-propagation'
import { formatPrice } from 'customer-flows/utils/format-price'

export enum BreakdownModeEnum {
  DEFAULT = 'DEFAULT',
  PARTIAL_TAXES_FEES_BREAKDOWN = 'PARTIAL_TAXES_FEES_BREAKDOWN',
  ITEMIZED_TAXES_FEES_DEFAULT = 'ITEMIZED_TAXES_FEES_DEFAULT',
  ITEMIZED_TAXES_FEES_BREAKDOWN = 'ITEMIZED_TAXES_FEES_BREAKDOWN'
}

type TaxKey = Extract<
  keyof PriceBreakdown,
  'cityTax' | 'stateTax' | 'flatTax' | 'foodAndBeverageTax' | 'otherTax' | 'retailTax'
>
type FeeKey = Extract<keyof PriceBreakdown, 'fees'>
type TaxAndFeeKey = Extract<keyof PriceBreakdown, TaxKey | FeeKey>

export const TAX_KEYS: TaxKey[] = ['cityTax', 'stateTax', 'flatTax', 'foodAndBeverageTax', 'otherTax', 'retailTax']
export const FEE_KEYS: FeeKey[] = ['fees']
export const TAXES_AND_FEES_KEYS: TaxAndFeeKey[] = [...FEE_KEYS, ...TAX_KEYS]

export const TAX_TRANSLATION_KEYS: Record<TaxAndFeeKey, string> = {
  cityTax: 'booking.cart-summary.city-tax',
  flatTax: 'booking.cart-summary.flat-tax',
  foodAndBeverageTax: 'booking.cart-summary.food-and-beverage-tax',
  otherTax: 'booking.cart-summary.other-tax',
  retailTax: 'booking.cart-summary.retail-tax',
  stateTax: 'booking.cart-summary.state-tax',
  fees: 'booking.cart-summary.convenience-fee'
}

type TaxesAndFeesSummaryArgs = {
  priceBreakdown?: PriceBreakdown
  breakdownMode: OrderTaxesFeesBreakdownMode | undefined
  pricingTransparency?: boolean
  isPaymentPage?: boolean
  isMobile?: boolean
  slot?: string
}

export default class TaxesAndFeesSummary extends Component<TaxesAndFeesSummaryArgs> {
  get shouldShowTooltip(): boolean {
    if (this.args.pricingTransparency) {
      return false
    }

    return [BreakdownModeEnum.PARTIAL_TAXES_FEES_BREAKDOWN, BreakdownModeEnum.ITEMIZED_TAXES_FEES_BREAKDOWN].includes(
      this.args.breakdownMode as BreakdownModeEnum
    )
  }

  get shouldShowSeparateLines(): boolean {
    // Pricing transparency overrides all other settings
    // Desktop: always show line-by-line when pricing transparency is enabled
    // Mobile: only show line-by-line on payment page when pricing transparency is enabled
    if (this.args.pricingTransparency) {
      if (this.args.isMobile) {
        return this.args.isPaymentPage ?? false
      }
      return true
    }

    return [BreakdownModeEnum.ITEMIZED_TAXES_FEES_DEFAULT, BreakdownModeEnum.ITEMIZED_TAXES_FEES_BREAKDOWN].includes(
      this.args.breakdownMode as BreakdownModeEnum
    )
  }

  get shouldShowMobileSummary(): boolean {
    return this.args.isMobile === true && this.args.isPaymentPage !== true
  }

  get taxesAndFeesAmountFormatted(): string {
    return this.args.priceBreakdown?.taxesAndFees?.formatted || ''
  }

  get totalTaxAmount(): string {
    let totalTax = 0
    let currency = this.args.priceBreakdown?.taxesAndFees?.currency ?? 'USD'

    TAX_KEYS.forEach((key) => {
      const taxItem = this.args.priceBreakdown?.[key]

      if (taxItem?.amount) {
        totalTax += parseFloat(taxItem.amount)
        currency = taxItem.currency || currency
      }
    })

    return formatPrice(totalTax, currency)
  }

  get totalFeesAmount(): string {
    let totalFees = 0
    let currency = this.args.priceBreakdown?.fees?.currency ?? 'USD'

    FEE_KEYS.forEach((key) => {
      const feeItem = this.args.priceBreakdown?.[key]

      if (feeItem?.amount) {
        totalFees += parseFloat(feeItem.amount)
        currency = feeItem.currency || currency
      }
    })

    return formatPrice(totalFees, currency)
  }

  get feesAmountFormatted(): string {
    return this.args.priceBreakdown?.fees?.formatted ?? ''
  }

  get taxBreakdownItems(): Array<{ label: string; amount: string }> {
    const items: Array<{ label: string; amount: string }> = []

    TAX_KEYS.forEach((key) => {
      const taxItem = this.args.priceBreakdown?.[key]

      if (taxItem?.amount && parseFloat(taxItem.amount) > 0) {
        items.push({
          label: TAX_TRANSLATION_KEYS[key],
          amount: taxItem.formatted
        })
      }
    })

    return items
  }

  get feeBreakdownItems(): Array<{ label: string; amount: string }> {
    const items: Array<{ label: string; amount: string }> = []

    FEE_KEYS.forEach((key) => {
      const feeItem = this.args.priceBreakdown?.[key]

      if (feeItem?.amount && parseFloat(feeItem.amount) > 0) {
        items.push({
          label: TAX_TRANSLATION_KEYS[key],
          amount: feeItem.formatted
        })
      }
    })

    return items
  }

  get combinedBreakdownItems(): Array<{ label: string; amount: string }> {
    return [...this.taxBreakdownItems, ...this.feeBreakdownItems]
  }

  get hasTaxesToShow(): boolean {
    return this.taxBreakdownItems.length > 0
  }

  get hasFeesToShow(): boolean {
    return this.feeBreakdownItems.length > 0
  }

  get taxesTooltipItems(): Array<{ label: string; amount: string }> {
    if (this.args.breakdownMode === BreakdownModeEnum.ITEMIZED_TAXES_FEES_BREAKDOWN) {
      return this.taxBreakdownItems
    }

    return [
      {
        label: 'booking.cart-summary.taxes-label',
        amount: this.totalTaxAmount
      }
    ]
  }

  get feesTooltipItems(): Array<{ label: string; amount: string }> {
    if (this.args.breakdownMode === BreakdownModeEnum.ITEMIZED_TAXES_FEES_BREAKDOWN) {
      return this.feeBreakdownItems
    }

    return [
      {
        label: 'booking.cart-summary.convenience-fee',
        amount: this.totalFeesAmount
      }
    ]
  }

  get combinedTooltipItems(): Array<{ label: string; amount: string }> {
    const items: Array<{ label: string; amount: string }> = []

    if (this.hasTaxesToShow) {
      items.push({
        label: 'booking.cart-summary.taxes-label',
        amount: this.totalTaxAmount
      })
    }

    if (this.hasFeesToShow) {
      items.push({
        label: 'booking.cart-summary.convenience-fee',
        amount: this.totalFeesAmount
      })
    }

    return items
  }

  /**
   * Returns the appropriate tooltip items for the mobile summary based on settings:
   * - If pricing transparency OR ITEMIZED_TAXES_FEES_BREAKDOWN: show each tax individually + convenience fees as 1 combined value
   * - If PARTIAL_TAXES_FEES_BREAKDOWN or ITEMIZED_TAXES_FEES_DEFAULT: show total taxes + convenience fees (2 lines)
   * - Otherwise (DEFAULT): no tooltip shown
   */
  get mobileSummaryTooltipItems(): Array<{ label: string; amount: string }> {
    const shouldShowItemizedTaxes =
      this.args.pricingTransparency || this.args.breakdownMode === BreakdownModeEnum.ITEMIZED_TAXES_FEES_BREAKDOWN

    const items: Array<{ label: string; amount: string }> = []

    if (shouldShowItemizedTaxes) {
      items.push(...this.taxBreakdownItems)
    } else {
      if (this.hasTaxesToShow) {
        items.push({
          label: 'booking.cart-summary.taxes-label',
          amount: this.totalTaxAmount
        })
      }
    }

    if (this.hasFeesToShow) {
      items.push({
        label: 'booking.cart-summary.convenience-fee',
        amount: this.totalFeesAmount
      })
    }

    return items
  }

  get shouldShowMobileSummaryTooltip(): boolean {
    if (this.args.pricingTransparency) {
      return true
    }

    return [
      BreakdownModeEnum.PARTIAL_TAXES_FEES_BREAKDOWN,
      BreakdownModeEnum.ITEMIZED_TAXES_FEES_BREAKDOWN,
      BreakdownModeEnum.ITEMIZED_TAXES_FEES_DEFAULT
    ].includes(this.args.breakdownMode as BreakdownModeEnum)
  }

  <template>
    {{#if this.shouldShowMobileSummary}}
      <div class='-mt-1 flex items-center gap-2' data-testid='taxes-and-fees-summary__mobile-summary'>
        <span class='text-reg-para-12 text-neutrals-300'>{{t
            'booking.cart-summary.taxes'
            totalTaxes=this.taxesAndFeesAmountFormatted
          }}</span>
        {{#if this.shouldShowMobileSummaryTooltip}}
          <il-tooltip
            {{stopClickPropagation}}
            tooltip-text={{t 'booking.cart-summary.taxes-and-fees-breakdown'}}
            tooltip-position='top'
            data-testid='taxes-and-fees-summary__mobile-summary-tooltip'
          >
            <div slot='content'>
              <div class='flex flex-col gap-1'>
                {{#each this.mobileSummaryTooltipItems as |item|}}
                  <div class='flex justify-between gap-4'>
                    <span class='text-component-bold-para-14 text-neutrals-350'>{{t item.label}}</span>
                    <span class='text-component-bold-para-14 text-neutrals-300'>{{item.amount}}</span>
                  </div>
                {{/each}}
              </div>
            </div>
            <div class='text-neutrals-300 h-5 w-5'>
              <Icon @src='info' />
            </div>
          </il-tooltip>
        {{/if}}
      </div>
    {{else if this.shouldShowSeparateLines}}
      {{#if @pricingTransparency}}
        {{#each this.taxBreakdownItems as |item|}}
          <il-list-item data-testid='taxes-and-fees-summary__tax-item'>
            <span>{{t item.label}}</span>
            <span slot='right' class='text-component-bold-para-14 text-neutrals-300'>{{item.amount}}</span>
          </il-list-item>
        {{/each}}

        <il-list-item data-testid='taxes-and-fees-summary__convenience-fee'>
          <span>{{t 'booking.cart-summary.convenience-fee'}}</span>
          <span slot='right' class='text-component-bold-para-14 text-neutrals-300'>{{this.totalFeesAmount}}</span>
        </il-list-item>
      {{else}}
        <il-list-item data-testid='taxes-and-fees-summary__taxes'>
          <div class='flex items-center gap-2'>
            <span>{{t 'booking.cart-summary.taxes-label'}}</span>
            {{#if (and this.shouldShowTooltip this.hasTaxesToShow)}}
              <il-tooltip
                {{stopClickPropagation}}
                tooltip-text={{t 'booking.cart-summary.taxes-breakdown'}}
                tooltip-position='top'
                data-testid='taxes-and-fees-summary__taxes-tooltip'
              >
                <div slot='content'>
                  <div class='flex flex-col gap-1'>
                    {{#each this.taxesTooltipItems as |item|}}
                      <div class='flex justify-between gap-4'>
                        <span class='text-component-bold-para-14 text-neutrals-350'>{{t item.label}}</span>
                        <span class='text-component-bold-para-14 text-neutrals-300'>{{item.amount}}</span>
                      </div>
                    {{/each}}
                  </div>
                </div>
                <div class='text-neutrals-300 h-5 w-5'>
                  <Icon @src='info' />
                </div>
              </il-tooltip>
            {{/if}}
          </div>
          <span
            slot='right'
            class='text-component-bold-para-14 text-neutrals-300'
            data-testid='taxes-and-fees-summary__taxes-amount'
          >{{this.totalTaxAmount}}</span>
        </il-list-item>

        {{#if this.hasFeesToShow}}
          <il-list-item data-testid='taxes-and-fees-summary__fees'>
            <div class='flex items-center gap-2'>
              <span>{{t 'booking.cart-summary.fees'}}</span>
              {{#if this.shouldShowTooltip}}
                <il-tooltip
                  {{stopClickPropagation}}
                  tooltip-text={{t 'booking.cart-summary.fees-breakdown'}}
                  tooltip-position='top'
                  data-testid='taxes-and-fees-summary__fees-tooltip'
                >
                  <div slot='content'>
                    <div class='flex flex-col gap-1'>
                      {{#each this.feesTooltipItems as |item|}}
                        <div class='flex justify-between gap-4'>
                          <span class='text-component-bold-para-14 text-neutrals-350'>{{t item.label}}</span>
                          <span class='text-component-bold-para-14 text-neutrals-300'>{{item.amount}}</span>
                        </div>
                      {{/each}}
                    </div>
                  </div>
                  <div class='text-neutrals-300 h-5 w-5'>
                    <Icon @src='info' />
                  </div>
                </il-tooltip>
              {{/if}}
            </div>
            <span
              slot='right'
              class='text-component-bold-para-14 text-neutrals-300'
              data-testid='taxes-and-fees-summary__fees-amount'
            >{{this.feesAmountFormatted}}</span>
          </il-list-item>
        {{/if}}
      {{/if}}
    {{else}}
      <il-list-item data-testid='taxes-and-fees-summary__combined'>
        <div class='flex items-center gap-2'>
          <span>{{t 'booking.cart-summary.taxes-and-fees'}}</span>
          {{#if this.shouldShowTooltip}}
            <il-tooltip
              {{stopClickPropagation}}
              tooltip-text={{t 'booking.cart-summary.taxes-and-fees-breakdown'}}
              tooltip-position='top'
              data-testid='taxes-and-fees-summary__combined-tooltip'
            >
              <div slot='content'>
                <div class='flex flex-col gap-1'>
                  {{#each this.combinedTooltipItems as |item|}}
                    <div class='flex justify-between gap-4'>
                      <span class='text-component-bold-para-14 text-neutrals-350'>{{t item.label}}</span>
                      <span class='text-component-bold-para-14 text-neutrals-300'>{{item.amount}}</span>
                    </div>
                  {{/each}}
                </div>
              </div>
              <div class='text-neutrals-300 h-5 w-5'>
                <Icon @src='info' />
              </div>
            </il-tooltip>
          {{/if}}
        </div>
        <span
          slot='right'
          class='text-component-bold-para-14 text-neutrals-300'
          data-testid='taxes-and-fees-summary__combined-amount'
        >{{this.taxesAndFeesAmountFormatted}}</span>
      </il-list-item>
    {{/if}}
  </template>
}
